import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, getQueryKey } from '@feature/utils';

import * as api from './LaboratoryApi';
import { Laboratory, LaboratoryInfoRequest, LaboratoryRequest } from './LaboratoryTypes';

const laboratoryKey = getQueryKey('laboratory');

export function getLaboratoriesOptions(): UseSuspenseQueryOptions<Laboratory[]> {
  return {
    queryKey: laboratoryKey.list(),
    queryFn: () => api.getAll(),
  };
}

export function getLaboratoriesOptionsForCenter(centerId: Id): UseSuspenseQueryOptions<Laboratory[]> {
  return {
    queryKey: laboratoryKey.as(['center-list', centerId]),
    queryFn: () => api.getAllForCenter(centerId),
  };
}

export function getLaboratoryOptions(laboratoryId: Id): UseSuspenseQueryOptions<Laboratory> {
  return {
    queryKey: laboratoryKey.one(laboratoryId),
    queryFn: () => api.get(laboratoryId),
  };
}

export function useLaboratoryUpdateInfo(laboratoryId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: LaboratoryInfoRequest) => api.updateInfo(laboratoryId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: laboratoryKey.root(),
      }),
  });
}

export function useLaboratoryEdit(laboratoryId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: LaboratoryRequest) => api.edit(laboratoryId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: laboratoryKey.root(),
      }),
  });
}

export function useLaboratoryCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: LaboratoryRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: laboratoryKey.root(),
      }),
  });
}

export function useLaboratoryDelete(laboratoryId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(laboratoryId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: laboratoryKey.root(),
      }),
  });
}
